/**
 * ConvexCollider2D Component
 *
 * Automatically generates a convex hull collision shape from
 * visible (non-transparent) pixels of a sprite.
 *
 * Requires a SpriteRenderer component on the same GameObject
 * to get the sprite source.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  field,
  customEditor,
} from "../attributes";

@component("core.ConvexCollider2D")
@customEditor("ConvexCollider2DEditor")
@displayName("Convex Collider 2D")
@icon("Hexagon")
@color("#00b894")
@category("Physics 2D")
@allowMultiple(false)
@requireComponent(["core.Transform", "core.SpriteRenderer"])
export class ConvexCollider2D {
  /** Offset from transform center */
  @field({ type: "vec2", label: "Offset" })
  offset: { x: number; y: number } = { x: 0, y: 0 };

  /**
   * Alpha threshold for determining visible pixels.
   * Pixels with alpha > threshold are considered solid.
   */
  @field({ type: "f32", label: "Alpha Threshold", min: 0, max: 255, step: 1, group: "Generation" })
  alphaThreshold: number = 128;

  /**
   * Simplification tolerance for reducing vertex count.
   * Higher values = fewer vertices but less accurate shape.
   * 0 = no simplification.
   */
  @field({ type: "f32", label: "Simplification", min: 0, max: 10, step: 0.1, group: "Generation" })
  simplification: number = 1;

  /**
   * Maximum number of vertices in the convex hull.
   * Set to 0 for automatic (no limit).
   */
  @field({ type: "i32", label: "Max Vertices", min: 0, max: 16, group: "Generation" })
  maxVertices: number = 0;

  /**
   * Detected optimal shape type:
   * - "polygon" = use convex polygon
   * - "box" = shape is close to axis-aligned box
   * - "circle" = shape is close to circle
   */
  @field({ type: "string", label: "Detected Shape", hidden: true })
  detectedShape: "polygon" | "box" | "circle" = "polygon";

  /** For box shape: half extents */
  @field({ type: "vec2", hidden: true })
  boxHalfExtents: { x: number; y: number } = { x: 0.5, y: 0.5 };

  /** For circle shape: radius */
  @field({ type: "f32", hidden: true })
  circleRadius: number = 0.5;

  /**
   * Whether to automatically regenerate hull when sprite changes.
   */
  @field({ type: "bool", label: "Auto Update", group: "Generation" })
  autoUpdate: boolean = true;

  /**
   * Cached convex hull points (in local space, CCW winding).
   * This is computed from the sprite and cached for physics.
   */
  @field({ type: "object", label: "Points", hidden: true })
  points: { x: number; y: number }[] = [];

  /**
   * Sprite asset GUID that was used to generate the current hull.
   * Used to detect when regeneration is needed.
   */
  @field({ type: "string", hidden: true })
  cachedSpriteId: string = "";

  /** Is trigger (no collision response, only events) */
  @field({ type: "bool", label: "Is Trigger" })
  isTrigger: boolean = false;

  /** Whether this collider casts 2D shadows */
  @field({ type: "bool", label: "Cast Shadow", group: "Shadow" })
  castShadow: boolean = false;

  /** Physics material */
  @field({ type: "f32", label: "Friction", min: 0, max: 1, step: 0.01, group: "Material" })
  friction: number = 0.4;

  @field({ type: "f32", label: "Bounciness", min: 0, max: 1, step: 0.01, group: "Material" })
  bounciness: number = 0;

  @field({ type: "f32", label: "Density", min: 0.01, step: 0.1, group: "Material" })
  density: number = 1;

  /** How to combine friction when colliding (0=Avg, 1=Min, 2=Max, 3=Multiply) */
  @field({ type: "i32", label: "Friction Combine", group: "Material", hidden: true })
  frictionCombine: number = 0;

  /** How to combine bounciness when colliding (0=Avg, 1=Min, 2=Max, 3=Multiply) */
  @field({ type: "i32", label: "Bounciness Combine", group: "Material", hidden: true })
  bouncinessCombine: number = 0;

  /** One-way platform - allows passing through from one side */
  @field({ type: "bool", label: "One-Way Platform", hidden: true })
  oneWay: boolean = false;

  /** Direction of one-way (normal pointing to "solid" side) */
  @field({ type: "vec2", label: "One-Way Direction", hidden: true })
  oneWayDirection: { x: number; y: number } = { x: 0, y: 1 };

  /** Collision layer */
  @field({ type: "i32", label: "Layer", hidden: true })
  layerMask: number = 1;

  /** Collision mask */
  @field({ type: "i32", label: "Collision Mask", hidden: true })
  collisionMask: number = 0xFFFF;
}
